import { useState, useMemo } from 'react';
import DateRangePicker from './DateRangePicker';
import { fmt$ } from '../utils/formatters';
import { getRange, fmtDateShort, fmtMonthYear, PRESETS } from '../utils/dateRange';
import { useConfirm } from '../lib/useConfirm';
import ConfirmDialog from './ConfirmDialog';
import { supabase } from '../lib/supabase';

const ALL_CATS=["Food","Beverages","Paper Goods","Retail Goods","Advertising & Marketing","Cintas (Cleaning)","General Business","Insurance","Workers Comp","Legal & Accounting","Office Expenses","Payroll Taxes","Payroll Prep Fees","Pest Control","POS Expenses","Repairs & Maintenance","Trash Removal","Travel","Uniforms","Utilities - Electric","Utilities - Internet","Utilities - Water/Sewer","Music/Entertainment","Meals","Miscellaneous"];

export default function ExpenseTracker({t,si,sb,sh,sd,mn,otherExp,purchases,setOtherExp,setPurchases,save,setSaveToast,saveToastTimer}){
const dlg=useConfirm();

function showToast(type,msg){
  if(!setSaveToast)return;
  clearTimeout(saveToastTimer?.current);
  setSaveToast({type,msg});
  if(saveToastTimer)saveToastTimer.current=setTimeout(()=>setSaveToast(null),type==='ok'?2000:15000);
}

function deleteEntry(entry){
  dlg.ask(`Delete ${entry.vendor} ($${fmt$(entry.amt)})? This cannot be undone.`,async()=>{
    if(entry.source==='Expense'){
      setOtherExp(p=>p.filter(x=>x.id!==entry.id));
      save.deleteOtherExpense(entry.id);
    }else{
      setPurchases(p=>p.filter(x=>x.id!==entry.id));
      save.deletePurchase(entry.id);
      // Also delete matching invoice record (enables re-upload)
      const{data:inv}=await supabase.from('invoices').select('id').eq('vendor',entry.vendor).eq('invoice_date',entry.date).limit(1);
      if(inv?.length){
        await supabase.from('invoice_line_items').delete().eq('invoice_id',inv[0].id);
        await supabase.from('invoices').delete().eq('id',inv[0].id);
      }
    }
    showToast('ok','Deleted');
  });
}
const[filterCat,setFilterCat]=useState("All");
const[preset,setPreset]=useState('this-month');
const[customStart,setCustomStart]=useState('');const[customEnd,setCustomEnd]=useState('');
const range=getRange(preset,{start:customStart,end:customEnd});

// Merge expenses + purchases into one list
const allEntries=useMemo(()=>{
  const entries=[];
  (otherExp||[]).forEach(e=>{
    if(e.date>=range.start&&e.date<=range.end)
      entries.push({id:e.id,date:e.date,category:e.category||'Miscellaneous',vendor:e.desc,desc:'',amt:e.amt,source:'Expense'});
  });
  (purchases||[]).forEach(p=>{
    if(p.date>=range.start&&p.date<=range.end)
      entries.push({id:p.id,date:p.date,category:p.category||'Food',vendor:p.vendor,desc:'',amt:p.amt,source:'Purchase'});
  });
  return entries.sort((a,b)=>b.date.localeCompare(a.date));
},[otherExp,purchases,range.start,range.end]);

const filtered=filterCat==="All"?allEntries:allEntries.filter(e=>e.category===filterCat);
const total=filtered.reduce((s,e)=>s+e.amt,0);

// Category breakdown
const byCat={};allEntries.forEach(e=>{byCat[e.category]=(byCat[e.category]||0)+e.amt;});
const grandTotal=allEntries.reduce((s,e)=>s+e.amt,0);

// Group by month
const byMonth={};
filtered.forEach(e=>{const mk=e.date.slice(0,7);if(!byMonth[mk])byMonth[mk]=[];byMonth[mk].push(e);});
const monthKeys=Object.keys(byMonth).sort().reverse();

// Source badge colors
const srcStyle=(s)=>s==='Purchase'?{background:"rgba(212,168,36,0.12)",color:"#d4a824"}:{background:"rgba(139,143,158,0.12)",color:"#8b8f9e"};

return(<div>
<h1 style={{fontSize:22,fontWeight:700,margin:"0 0 20px"}}>Expense Tracker</h1>

{/* Date range selector */}
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:"14px 18px",marginBottom:16}}>
<div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
{PRESETS.map(p=><button key={p.id} onClick={()=>setPreset(p.id)} style={{...sb,background:preset===p.id?t.bl:t.sf,color:preset===p.id?"#fff":t.tm,border:`1px solid ${preset===p.id?t.bl:t.bd}`,padding:"6px 12px",fontSize:10}}>{p.label}</button>)}
</div>
{preset==='custom'&&<DateRangePicker start={customStart} end={customEnd} onChange={(s,e)=>{setCustomStart(s);setCustomEnd(e);}} t={t} si={si}/>}
<div style={{fontSize:11,color:t.tm,marginTop:8}}>{fmtDateShort(range.start)} — {fmtDateShort(range.end)}</div>
</div>

{/* Filters */}
<div style={{display:"flex",gap:12,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
<select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{...si,width:220}}><option value="All">All Categories</option>{ALL_CATS.map(c=><option key={c}>{c}</option>)}</select>
<div style={{marginLeft:"auto",fontFamily:mn,fontSize:14,fontWeight:600,color:t.ac}}>{filtered.length} entries — ${fmt$(total)}</div>
</div>

{/* Category breakdown */}
{Object.keys(byCat).length>0&&<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:18,marginBottom:16}}>
<div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Period Summary — ${fmt$(grandTotal)}</div>
<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
{Object.entries(byCat).sort((a,b)=>b[1]-a[1]).map(([cat,v])=>
<div key={cat} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${t.bd}`}}>
<span style={{fontSize:11,color:t.tm}}>{cat}</span>
<span style={{fontFamily:mn,fontSize:11,color:t.ac}}>${fmt$(v)}</span>
</div>)}
</div>
</div>}

{/* Entries grouped by month */}
{filtered.length===0?<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:20,textAlign:"center",color:t.td}}>No entries in this period</div>:
monthKeys.map(mk=>{const items=byMonth[mk];const monthSub=items.reduce((s,e)=>s+e.amt,0);return(
<div key={mk} style={{marginBottom:16}}>
<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
<div style={{fontSize:13,fontWeight:600}}>{fmtMonthYear(mk+'-15')}</div>
<div style={{fontFamily:mn,fontSize:12,fontWeight:600,color:t.ac}}>${fmt$(monthSub)}</div>
</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:"hidden"}}>
<table style={{width:"100%",borderCollapse:"collapse"}}>
<thead><tr style={{background:t.sf}}>{["Date","Category","Vendor","Amount","Source",""].map(h=><th key={h} style={sh}>{h}</th>)}</tr></thead>
<tbody>{items.map(e=>(
<tr key={e.id} style={{borderBottom:`1px solid ${t.bd}`}}>
<td style={{...sd,fontSize:12}}>{fmtDateShort(e.date)}</td>
<td style={sd}><span style={{fontSize:9,padding:"2px 5px",borderRadius:3,background:"rgba(59,130,246,0.12)",color:t.bl}}>{e.category}</span></td>
<td style={{...sd,fontWeight:500,fontSize:12}}>{e.vendor}</td>
<td style={{...sd,fontFamily:mn,fontWeight:600,color:t.ac}}>${fmt$(e.amt)}</td>
<td style={sd}><span style={{fontSize:9,padding:"2px 5px",borderRadius:3,...srcStyle(e.source)}}>{e.source}</span></td>
<td style={sd}><button onClick={()=>deleteEntry(e)} style={{...sb,background:"transparent",color:t.ac,padding:"2px 6px",fontSize:10}}>✕</button></td>
</tr>))}</tbody></table>
</div></div>);})}

{/* Total */}
{filtered.length>0&&<div style={{display:"flex",justifyContent:"flex-end",padding:"12px 0"}}>
<div style={{fontFamily:mn,fontSize:16,fontWeight:700,color:t.ac}}>Total: ${fmt$(total)}</div>
</div>}
{dlg.pending&&<ConfirmDialog message={dlg.pending.message} onConfirm={dlg.confirm} onCancel={dlg.cancel} t={t}/>}
</div>);
}
